// LogbookPouchRepo.ts
import PouchDB from "@/repos/PouchDB";
import type { LogbookType, QsoType } from "@/types";
import type { BaseRepoInterface } from "@/interfaces";
import { v4 as uuidv4 } from "uuid";
import { LOGBOOK_ID_PREFIX } from "@/constants";
import QsoService from "@/services/QsoService";
import { deleteDocIfPresent } from "@/repos/pouchHelpers";
import { captureMessage } from "@/utils/sentry";

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
// NOTE: putting this method here lets our callers know we for sure have a
// delete and then it's happy.
interface LogbookRepoInterface extends BaseRepoInterface<LogbookType> {
  delete(id: string): Promise<LogbookType | null>;
  update(logbook: LogbookType): Promise<LogbookType>;
  updateStickyFieldValues(logbookId: string, stickyFieldValues: Partial<QsoType>): Promise<void>;
  search(query: string, limit?: number): Promise<LogbookType[]>;
}
function rankSearchResults(query: string, results: LogbookType[]): LogbookType[] {
  const q = query.toLowerCase();
  const rankedResults = results.map((doc) => {
    // A logbook may have a null/missing title (legacy/corrupt data); coalesce so
    // ranking never throws on `.toLowerCase()` and break search for everyone.
    const title = (doc.title ?? "").toLowerCase();
    let score = 0;

    // Exact match
    if (title === q) {
      score = 3;
    }
    // Prefix match
    else if (title.startsWith(q)) {
      score = 2;
    }
    // Partial match
    else if (title.includes(q)) {
      score = 1;
    }

    return { doc, score };
  });

  // Sort by score in descending order (highest score first)
  rankedResults.sort((a, b) => b.score - a.score);

  // Extract and return sorted documents
  return rankedResults.map((item) => item.doc);
}

const LogbookPouchRepo: LogbookRepoInterface = {
  create: async (params: { attrs: Partial<LogbookType> }): Promise<LogbookType> => {
    const logId = `${LOGBOOK_ID_PREFIX}:${uuidv4()}`;
    const now = new Date().toISOString();
    // Required base fields must ALWAYS be present and must win over `...attrs`,
    // otherwise a logbook can be persisted with a null/undefined title,
    // createdAt, or template -- which crashes the logbook-list sort (see
    // findAll) and blanks the entire list on every device once it syncs.
    // Caller-provided values are preserved where present; only gaps are filled.
    const newLogbook = {
      ...params.attrs,
      _id: logId,
      createdAt: params.attrs.createdAt ?? now,
      updatedAt: now,
      qsoCount: params.attrs.qsoCount ?? 0,
      title: params.attrs.title?.trim() || "Untitled Logbook",
      template: params.attrs.template ?? "GENERIC",
    };

    await PouchDB.upsert(logId, () => newLogbook);
    return PouchDB.get(logId);
  },

  update: async (logbook: LogbookType): Promise<LogbookType> => {
    const updatedAt = new Date().toISOString();
    logbook.updatedAt = updatedAt;
    logbook.title = logbook.title?.trim();

    // Update qsoCount before upserting
    const qsoCount = await QsoService.countAllByLogbook(logbook._id);
    logbook.qsoCount = qsoCount;

    // Use upsert to apply the changes
    await PouchDB.upsert(logbook._id, () => logbook);

    // Return the updated logbook from the database
    return PouchDB.get(logbook._id);
  },

  findAll: async (): Promise<LogbookType[]> => {
    const result = await PouchDB.allDocs({
      include_docs: true,
      startkey: `${LOGBOOK_ID_PREFIX}:`,
      endkey: `${LOGBOOK_ID_PREFIX}:\ufff0`,
    });

    return result.rows
      .map((row) => row.doc as unknown as LogbookType)
      // Coalesce createdAt (fall back to updatedAt, then empty string) so a
      // single logbook with a missing createdAt can't throw here and blank the
      // whole list. Newest-first.
      .sort((a, b) =>
        (b.createdAt ?? b.updatedAt ?? "").localeCompare(a.createdAt ?? a.updatedAt ?? "")
      );
  },

  findOne: async (id: string): Promise<LogbookType> => {
    return PouchDB.get(id);
  },
  search: async (query: string, limit?: number): Promise<LogbookType[]> => {
    const selector = {
      _id: { $gte: `${LOGBOOK_ID_PREFIX}:`, $lte: `${LOGBOOK_ID_PREFIX}:\ufff0` },
      title: { $regex: new RegExp(escapeRegExp(query), 'i') }
    };
    const findOptions: any = { selector };

    // Add limit to findOptions if it's defined
    // if (limit) {
    //   findOptions.limit = limit;
    // }

    const result = await PouchDB.find(findOptions);
    const results = result.docs as unknown as LogbookType[];
    const sortedResults = rankSearchResults(query, results);

    // Apply limit if specified
    return limit ? sortedResults.slice(0, limit) : sortedResults;
  },
  updateStickyFieldValues: async (logbookId: string, stickyFieldValues: Partial<QsoType>): Promise<void> => {
    await PouchDB.upsert(logbookId, (doc: any) => {
      // Sticky-field updates must only ever modify an EXISTING logbook.
      // pouchdb-upsert passes `{}` (no _rev) when the doc is missing; returning
      // a falsy value aborts the write. Without this guard a sticky update on a
      // missing id materializes a logbook that has only stickyFieldValues +
      // updatedAt (no title/createdAt/template) -- the exact corrupted shape
      // behind the 2026-06 list-blanking incident, which then syncs everywhere.
      if (!doc || !doc._rev) {
        // The guard correctly prevents resurrecting a corrupt shell logbook, but
        // a caller writing sticky fields to a missing/deleted logbook is an
        // upstream bug. Surface it (not just console, which is invisible in prod)
        // so we can alert on this firing unexpectedly instead of swallowing it.
        console.warn(
          `updateStickyFieldValues: logbook ${logbookId} does not exist; aborting sticky-field write`
        );
        captureMessage("updateStickyFieldValues called for missing logbook", "warning", {
          logbookId,
          scope: "LogbookPouchRepo.updateStickyFieldValues",
        });
        return false;
      }
      return {
        ...doc,
        stickyFieldValues,
        updatedAt: new Date().toISOString(),
      };
    });
  },

  delete: async (id: string): Promise<LogbookType | null> => {
    // Idempotent + sync-race safe: resolves null when the logbook is already
    // gone (HAMRS-1E). See deleteDocIfPresent.
    return deleteDocIfPresent<LogbookType>(id);
  },
};

export default LogbookPouchRepo;
